<template>
  <div>
    <AppDialog
      :title="dialogTitle"
      :show="showDialog"
      :loading="loading"
      class="holiday-setup-dialog"
      append-to-body
      position="centered"
      @close="closeAllDialogs"
    >
      <ValidationObserver ref="dialogObserver">
        <HolidaySetupForm
          :holiday="holiday"
          :organization-tz="organizationTz"
          :original-recurring="originalRecurring"
          :overlap-shift-count="overlapShiftCount"
          :loading="loading"
          editing
          @holiday-updated="handleHolidayUpdated"
          @members-updated="handleMembersUpdated"
        />
      </ValidationObserver>
      <template slot="footer">
        <div class="d-flex align-items-end gap-15 flex-row-reverse">
          <AppButton
            type="primary"
            :disabled="saving"
            @click="update"
          >
            Save
          </AppButton>
          <AppButton @click="closeAllDialogs">Cancel</AppButton>
        </div>
      </template>
    </AppDialog>

    <ShiftOverlapConfirmationDialog
      :show="showShiftOverlapConfirmationDialog"
      :overlap-shift-count="overlapShiftCount"
      :saving="saving"
      @update-remove-shift-field="handleRemoveShiftField"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import { HolidaysResource } from '~/resources';
import HolidaySetupForm from '~/components/holidays/HolidaySetupForm.vue';
import ShiftOverlapConfirmationDialog from '~/components/ShiftOverlapConfirmationDialog.vue';
import HolidayMixin from '~/mixins/holidays/HolidayMixin';

export default {
  name: 'HolidaySetupDialog',
  components: {
    HolidaySetupForm,
    ShiftOverlapConfirmationDialog,
  },
  mixins: [HolidayMixin],
  data: () => ({
    showDialog: false,
    holiday: {},
    holidayId: null,
    originalRecurring: false,
    overlapShiftCount: 0,
    overlapShiftError: false,
    showShiftOverlapConfirmationDialog: false,
    saving: false,
    loading: false,
    originalHolidayName: '',
  }),
  computed: {
    dialogTitle() {
      return this.originalHolidayName ? `Edit ${this.originalHolidayName}` : 'Edit Holiday';
    },
  },
  methods: {
    async toggleDialog(holidayId) {
      try {
        this.loading = true;
        this.showDialog = true;
        this.holidayId = holidayId;
        const params = { id: this.holidayId };
        const response = await HolidaysResource.edit(params, null, { abortReload: true });

        if (response.holiday) {
          this.holiday = { ...this.holiday, ...response.holiday, remove_shifts: false };
          this.originalHolidayName = this.holiday.name;
          this.originalRecurring = response.holiday.recurring;
          this.checkShiftOverlaps();
          this.loading = false;
        } else {
          throw new Error('Unable to fetch data for the holiday edition.');
        }
      } catch (error) {
        this.closeAllDialogs();
        await this.$appMessage('Unable to fetch data for the holiday update. Try again later.');
      }
    },
    closeAllDialogs() {
      this.showDialog = false;
      this.showShiftOverlapConfirmationDialog = false;
      this.overlapShiftCount = 0;
      this.loading = false;
    },
    async handleRemoveShiftField(field, value) {
      this.handleHolidayUpdated(field, value);
      await this.update();
    },
    async update() {
      this.performHolidayAction('update');
    },
    async updateHoliday() {
      try {
        const params = { id: this.holidayId };
        const holidayParams = {
          name: this.holiday.name,
          date: moment(this.holiday.date).format('YYYY-MM-DD'),
          amount_used: this.holiday.amount_used,
          remove_shifts: this.holiday.remove_shifts,
          recurring: this.holiday.recurring,
        };
        const response = await HolidaysResource.update(
          params,
          { holiday: holidayParams, ...(this.overlapShiftError && { overlap_shifts_error: this.overlapShiftError }) },
          { abortReload: true },
        );

        if (response.success) {
          this.closeAllDialogs();
          window.location.reload();
        } else if (response.errors) {
          this.$refs.dialogObserver.setErrors(response.errors);
          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;

          this.$appMessage('The holiday cannot be updated.');
        } else {
          throw new Error('Unable to update the holiday.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;
          this.$appMessage('Unexpected error. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
